import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { formatInTimeZone } from "shared/lib/utils";

import { Button as PostiesButton } from "scmp-app/components/posties/button";
import type { eventsPostiesPostiesEvent$key } from "scmp-app/queries/__generated__/eventsPostiesPostiesEvent.graphql";

import {
  Button,
  Container,
  Content,
  Cover,
  Date,
  Header,
  StyledImage,
  StyledOffsetContainer,
} from "./styles";

type Props = {
  className?: string;
  reference: eventsPostiesPostiesEvent$key;
};

export const Events: FunctionComponent<Props> = ({ className, reference }) => {
  const event = useFragment(
    graphql`
      fragment eventsPostiesPostiesEvent on PostiesEvent {
        urlAlias
        startDate
        endDate
        name
        summary {
          text
        }
        eventLabel
        images {
          title
          size1280x720: style(filter: { style: "wide_landscape" }) {
            url
          }
          size700x440: style(filter: { style: "700x440" }) {
            url
          }
        }
      }
    `,
    reference,
  );

  const mainImage = first(event.images);

  const variantSpecs = {
    desktopUp: { aspectRatio: "16/9", url: mainImage?.size1280x720?.url, width: 1280 },
    mobileUp: { aspectRatio: "16/9", url: mainImage?.size700x440?.url, width: 700 },
    tabletUp: { aspectRatio: "16/9", url: mainImage?.size700x440?.url, width: 700 },
  };
  const mapVariant = (viewportSize: "desktopUp" | "mobileUp" | "tabletUp") => ({
    aspectRatio: variantSpecs[viewportSize].aspectRatio,
    url: variantSpecs[viewportSize].url ?? "",
    width: variantSpecs[viewportSize].width,
  });

  const responsiveVariant = {
    desktopUp: mapVariant("desktopUp"),
    mobileUp: mapVariant("mobileUp"),
    tabletUp: mapVariant("tabletUp"),
  };

  const imageUrl = mainImage?.size700x440?.url ?? "";

  const endDate = event?.endDate && `${formatInTimeZone(event.endDate ?? "", "d MMM, yyyy")}`;
  const startDate = event?.startDate && `${formatInTimeZone(event.startDate ?? "", "d MMM, yyyy")}`;
  let date;
  if (!startDate && !endDate) {
    date = "";
  } else if (endDate === startDate || !startDate) {
    date = endDate;
  } else if (endDate) {
    date = `${startDate} - ${endDate}`;
  } else {
    date = startDate;
  }
  const label = event?.eventLabel ?? "";
  const isLongLabel = label.length > 5;

  if (!event) return null;
  return (
    <Container className={className}>
      <Header>{event?.name}</Header>
      <Date>{date}</Date>
      <Cover $isLongLabel={isLongLabel} $label={label}>
        <StyledOffsetContainer borderRadius="20px">
          <StyledImage
            $aspectRatio="16/9"
            $height="auto"
            $objectFit="cover"
            $width="100%"
            alt={mainImage?.title ?? ""}
            disableLinkingData
            responsiveVariant={responsiveVariant}
            src={imageUrl}
          />
        </StyledOffsetContainer>
      </Cover>
      <Content>{event?.summary?.text}</Content>
      <Button>
        <PostiesButton pathname={event?.urlAlias} variant="link">
          MORE DETAILS
        </PostiesButton>
      </Button>
    </Container>
  );
};

Events.displayName = "Events";
